import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Hourglass,
  type LucideIcon,
  MinusCircle,
  ShieldCheck,
  SkipForward,
} from "lucide-react";
import type { BundleRelevanceStatus } from "./useBundleRelevanceStatusesForEncounter";

export interface BundleRelevanceStatusPresentation {
  label: string;
  icon: LucideIcon;
  tooltip: string;
  colorClass: string;
}

// always_relevant_bundle_type is no longer written (see content_exempt_bundle_type below) but stays
// recognised here for ledger rows written before that change.
const AUTO_RELEVANT_REASONS = new Set([
  "always_relevant_bundle_type",
  "always_relevant_bundle_source",
  "always_relevant_source_bundle_type",
]);
const AUTO_NOT_RELEVANT_REASONS = new Set(["content_exempt_bundle_type"]);
const EMPTY_BODY_REASONS = new Set(["sentinel_body", "empty_body"]);
// Every supersession reason shares this prefix, so a new one needs no change here.
const isSupersededReason = (reason: string) => reason.startsWith("superseded_by_");

export function verdictLabel(relevant: boolean | null): string {
  if (relevant === true) return "Relevant";
  if (relevant === false) return "Not relevant";
  return "Unknown";
}

function describeDecidedNoReviewItem(
  status: Extract<BundleRelevanceStatus, { kind: "decided-no-review-item" }>,
): BundleRelevanceStatusPresentation {
  if (AUTO_RELEVANT_REASONS.has(status.reason ?? "")) {
    return {
      label: "Always relevant",
      icon: ShieldCheck,
      tooltip: "Automatically marked relevant — doesn't need AI/human review.",
      colorClass: "bg-blue-100 text-blue-800",
    };
  }

  if (isSupersededReason(status.reason)) {
    return {
      label: "Superseded",
      icon: SkipForward,
      tooltip: "Automatically marked not relevant — superseded by a newer or higher-priority bundle.",
      colorClass: "bg-slate-100 text-slate-700",
    };
  }

  if (AUTO_NOT_RELEVANT_REASONS.has(status.reason ?? "")) {
    return {
      label: "Excluded from summary",
      icon: SkipForward,
      tooltip:
        "Automatically excluded from the codeable summary — this document type carries no summary content, so it is never judged.",
      colorClass: "bg-slate-100 text-slate-700",
    };
  }

  if (status.reason === "never_relevant_bundle_type") {
    return {
      label: "Never relevant",
      icon: SkipForward,
      tooltip: "Automatically marked not relevant — this document type is never relevant to this specialty's coding.",
      colorClass: "bg-slate-100 text-slate-700",
    };
  }

  if (EMPTY_BODY_REASONS.has(status.reason ?? "")) {
    return {
      label: "No content",
      icon: SkipForward,
      tooltip: "Automatically marked not relevant — the extracted document body was empty.",
      colorClass: "bg-slate-100 text-slate-700",
    };
  }

  const verdict = `AI verdict: ${verdictLabel(status.relevant)}`;
  const tooltip = status.reason
    ? `${verdict} — ${status.reason}. Not currently queued for human review.`
    : `${verdict}. Not currently queued for human review.`;

  return {
    label: status.relevant ? "Relevant (unreviewed)" : "Not relevant (unreviewed)",
    icon: status.relevant ? ShieldCheck : SkipForward,
    tooltip,
    colorClass: status.relevant ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700",
  };
}

export function describeBundleRelevanceStatus(status: BundleRelevanceStatus): BundleRelevanceStatusPresentation {
  switch (status.kind) {
    case "not-assessed":
      return {
        label: "Not assessed",
        icon: Clock,
        tooltip: "Not yet evaluated for relevance to this encounter.",
        colorClass: "bg-gray-100 text-gray-700",
      };
    case "assessment-error":
      return {
        label: "Assessment failed",
        icon: AlertTriangle,
        tooltip: `Relevance assessment failed: ${status.reason ?? "unknown error"}.`,
        colorClass: "bg-red-100 text-red-800",
      };
    case "decided-no-review-item":
      return describeDecidedNoReviewItem(status);
    case "pending-review":
      return {
        label: "Pending review",
        icon: Hourglass,
        tooltip: `AI verdict: ${verdictLabel(status.reviewItem.aiRelevant)}. Awaiting human review.`,
        colorClass: "bg-amber-100 text-amber-800",
      };
    case "reviewed": {
      const { reviewedRelevant } = status.reviewItem;
      return {
        label: "Reviewed",
        icon: reviewedRelevant === true ? CheckCircle2 : reviewedRelevant === false ? MinusCircle : AlertTriangle,
        tooltip: `Reviewed: ${verdictLabel(reviewedRelevant)}.`,
        colorClass:
          reviewedRelevant === true
            ? "bg-green-100 text-green-800"
            : reviewedRelevant === false
              ? "bg-slate-100 text-slate-700"
              : "bg-amber-100 text-amber-800",
      };
    }
  }
}
